import React, { useState, useEffect, useCallback } from 'react'
import { fetchRequests, updateRequest, uploadRequestFile } from '../lib/api'
import SpecialBadge from './SpecialBadge'

const TYPE_LABELS = {
  evisa:  '🌐 E-виза',
  ticket: '✈️ Авиабилет',
  visa:   '📄 Виза',
  hotel:  '🏨 Отель',
  free:   '📝 Свободная',
  sos:    '🆘 Экстренное',
}

const STATUS_LABELS = {
  pending:   { label: 'Ожидает',    color: '#EF9F27', bg: '#FFF8EC' },
  approved:  { label: 'Принята',    color: '#639922', bg: '#F0F7E6' },
  rejected:  { label: 'Отклонена', color: '#E24B4A', bg: '#FEF0F0' },
  done:      { label: 'Выполнена', color: '#888',    bg: '#F5F5F5' },
  cancelled: { label: 'Отменена',  color: '#9B59B6', bg: '#F5EEF8' },
}

function Badge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: '#888', bg: '#F5F5F5' }
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  )
}

function fmtDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function RequestDetail({ req, onClose, onStatusChange, isAdmin, darkMode }) {
  const [comment, setComment] = useState('')
  const [showReject, setShowReject] = useState(false)
  const [showCancel, setShowCancel] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [files, setFiles] = useState(req.files || [])
  const d = req.data || {}

  async function handleApprove() {
    setSaving(true)
    await updateRequest(req.id, { status: 'approved', resolved_at: new Date().toISOString() })
    onStatusChange()
    setSaving(false)
  }

  async function handleReject() {
    if (!comment.trim()) return
    setSaving(true)
    await updateRequest(req.id, { status: 'rejected', notes: comment.trim(), resolved_at: new Date().toISOString() })
    onStatusChange()
    setSaving(false)
  }

  async function handleCancel() {
    if (!cancelReason.trim()) return
    setSaving(true)
    await updateRequest(req.id, { status: 'cancelled', notes: cancelReason.trim(), resolved_at: new Date().toISOString() })
    try {
      await fetch('/api/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ requestId: req.id, action: 'cancelled', reason: cancelReason.trim() }),
      })
    } catch {}
    onStatusChange()
    setSaving(false)
  }

  async function handleDone() {
    setSaving(true)
    await updateRequest(req.id, { status: 'done', resolved_at: new Date().toISOString() })
    try {
      const nr = await fetch('/api/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId: req.id, action: 'done' }),
      })
      const nj = await nr.json()
      if (!nj.ok) alert('Notify error: ' + JSON.stringify(nj))
    } catch (err) {
      alert('Notify fetch error: ' + err.message)
    }
    onStatusChange()
    setSaving(false)
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const result = await uploadRequestFile(req.id, file)
      const newFile = { name: file.name, url: result.url }
      const updated = [...files, newFile]
      await updateRequest(req.id, { files: updated })
      setFiles(updated)
    } catch (err) {
      alert('Ошибка: ' + err.message)
    }
    setUploading(false)
    e.target.value = ''
  }

  async function handleFileDelete(idx) {
    const updated = files.filter((_, i) => i !== idx)
    await updateRequest(req.id, { files: updated })
    setFiles(updated)
  }

  function renderData() {
    if (req.type === 'ticket') return (
      <>
        <Row label="Кандидат" value={d.candidate_name} />
        <Row label="Маршрут" value={d.from && d.to ? `${d.from} → ${d.to}` : '—'} />
        <Row label="Дата вылета" value={d.departure_date} />
        {d.departure_time && <Row label="Время (местное)" value={d.departure_time} />}
        {d.comment && <Row label="Комментарий" value={d.comment} />}
      </>
    )
    if (req.type === 'evisa') return (
      <>
        <Row label="Кандидат" value={d.candidate_name} />
        <Row label="Страна" value={d.country} />
        <Row label="Дата въезда" value={d.entry_date} />
        {d.comment && <Row label="Комментарий" value={d.comment} />}
      </>
    )
    if (req.type === 'visa') return (
      <>
        <Row label="Кандидат" value={d.candidate_name} />
        <Row label="Страна / посольство" value={d.country} />
        <Row label="Дата подачи" value={d.submission_date} />
        <Row label="Документы готовы" value={d.docs_ready} />
        {d.comment && <Row label="Комментарий" value={d.comment} />}
      </>
    )
    if (req.type === 'hotel') return (
      <>
        <Row label="Кандидат" value={d.candidate_name} />
        <Row label="Город" value={d.city} />
        <Row label="Заезд" value={d.check_in} />
        <Row label="Выезд" value={d.check_out} />
        <Row label="Человек" value={d.guests} />
        {d.comment && <Row label="Требования" value={d.comment} />}
      </>
    )
    if (req.type === 'free') return (
      <>
        <Row label="Тема" value={d.subject} />
        <Row label="Описание" value={d.description} />
        <Row label="Срочность" value={d.urgency} />
      </>
    )
    if (req.type === 'sos') return (
      <div style={{ fontSize: 14, color: '#222', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{d.message || '—'}</div>
    )
    return null
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: darkMode ? '#1a1f2e' : 'white', color: darkMode ? '#e2e8f0' : undefined, borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        {req.type === 'sos' && (
          <div style={{ background: '#E24B4A', color: 'white', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 13, fontWeight: 600, textAlign: 'center', letterSpacing: '0.05em' }}>
            🚨 ЭКСТРЕННОЕ СООБЩЕНИЕ 🚨
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <span style={{ fontSize: 13, color: '#aaa', fontWeight: 500 }}>Заявка #{req.id}</span>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, color: darkMode ? '#e2e8f0' : undefined }}>{TYPE_LABELS[req.type] || req.type}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge status={req.status} />
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa' }}>✕</button>
          </div>
        </div>

        <div style={{ background: darkMode ? '#252b3b' : '#fafaf8', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <Row label="Оператор" value={req.operator_name || '—'} />
          <Row label="Дата создания" value={fmtDate(req.created_at)} />
          {req.resolved_by && <Row label="Обработал" value={req.resolved_by} />}
        </div>

        <div style={{ background: darkMode ? '#1e2535' : '#f4f6fb', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          {renderData()}
        </div>

        {req.notes && req.status === 'rejected' && (
          <div style={{ background: '#FEF0F0', border: '1px solid #E24B4A33', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#E24B4A', fontWeight: 600, marginBottom: 4 }}>ПРИЧИНА ОТКЛОНЕНИЯ</div>
            <div style={{ fontSize: 13, color: '#333' }}>{req.notes}</div>
          </div>
        )}
        {req.notes && req.status === 'cancelled' && (
          <div style={{ background: '#F5EEF8', border: '1px solid #9B59B633', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#9B59B6', fontWeight: 600, marginBottom: 4 }}>ПРИЧИНА ОТМЕНЫ</div>
            <div style={{ fontSize: 13, color: '#333' }}>{req.notes}</div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 8 }}>📎 ДОКУМЕНТЫ</div>
          {files.length === 0 && <div style={{ fontSize: 12, color: '#bbb', marginBottom: 8 }}>Файлы не прикреплены</div>}
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.name}</a>
              {isAdmin && <button onClick={() => handleFileDelete(i)} style={{ background: 'none', border: 'none', color: '#E24B4A', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>×</button>}
            </div>
          ))}
          {isAdmin && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#185FA5', cursor: 'pointer', padding: '6px 10px', border: '1px dashed #B5D4F4', borderRadius: 8 }}>
              {uploading ? 'Загрузка...' : '+ Прикрепить файл'}
              <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} disabled={uploading} />
            </label>
          )}
        </div>

        {isAdmin && req.status === 'pending' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            {!showReject ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleApprove} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#639922', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                  ✅ Принять
                </button>
                <button onClick={() => setShowReject(true)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#E24B4A', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                  ❌ Отклонить
                </button>
              </div>
            ) : (
              <div>
                <textarea value={comment} onChange={e => setComment(e.target.value)}
                  placeholder="Укажите причину отклонения..."
                  style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0dc', padding: '10px 12px', fontSize: 13, resize: 'vertical', minHeight: 80, boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={handleReject} disabled={saving || !comment.trim()} style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: '#E24B4A', color: 'white', fontWeight: 600, cursor: 'pointer' }}>
                    Подтвердить отклонение
                  </button>
                  <button onClick={() => setShowReject(false)} style={{ padding: '9px 16px', borderRadius: 8, border: `1px solid ${darkMode ? '#2d3448' : '#e0e0dc'}`, background: darkMode ? '#252b3b' : 'white', color: darkMode ? '#e2e8f0' : undefined, cursor: 'pointer' }}>
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isAdmin && req.status === 'approved' && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={() => { if (window.confirm('Отметить заявку как выполненную?')) handleDone() }} disabled={saving} style={{ width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: '#1a1a1a', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              ✔️ Отметить выполненной
            </button>
            {!showCancel ? (
              <button onClick={() => setShowCancel(true)} disabled={saving} style={{ width: '100%', padding: '9px', borderRadius: 10, border: '1px solid #9B59B6', background: 'white', color: '#9B59B6', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                ✕ Отменить заявку
              </button>
            ) : (
              <div style={{ border: '1px solid #9B59B6', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, color: '#9B59B6', fontWeight: 600, marginBottom: 6 }}>Причина отмены</div>
                <textarea
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="Укажите причину..."
                  rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '7px 10px', border: '1px solid #e0e0dc', borderRadius: 8, resize: 'none', outline: 'none', fontFamily: 'inherit', marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={handleCancel} disabled={saving || !cancelReason.trim()} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#9B59B6', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                    Подтвердить отмену
                  </button>
                  <button onClick={() => { setShowCancel(false); setCancelReason('') }} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e0e0dc', background: 'white', cursor: 'pointer', fontSize: 13, color: '#888' }}>
                    Назад
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {isAdmin && req.status === 'done' && (
          <button onClick={async () => {
            if (!window.confirm('Вернуть заявку в статус "Принята"?')) return
            setSaving(true)
            await updateRequest(req.id, { status: 'approved', resolved_at: null })
            onStatusChange()
            setSaving(false)
          }} disabled={saving} style={{ width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: '#639922', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 14, marginTop: 8 }}>
            ↩ Вернуть в "Принята"
          </button>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: '#888', minWidth: 120, flexShrink: 0 }}>{label}:</span>
      <span style={{ color: '#222', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  )
}

const TYPES = ['', 'sos', 'evisa', 'ticket', 'visa', 'hotel', 'free']
const STATUSES = ['', 'pending', 'approved', 'rejected', 'cancelled', 'done']
const OPERATORS = ['', 'Алексей', 'Николай', 'Данила']

export default function RequestsPage({ onClose, isAdmin, darkMode }) {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterOp, setFilterOp] = useState('')
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchRequests()
      setRequests(data || [])
    } catch { setRequests([]) }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  function handleStatusChange() {
    load()
    setSelected(null)
  }

  const filtered = requests.filter(r => {
    if (filterType && r.type !== filterType) return false
    if (filterStatus && r.status !== filterStatus) return false
    if (filterOp && r.operator_name !== filterOp) return false
    if (search) {
      const d = r.data || {}
      const q = search.toLowerCase()
      return (
        String(r.id).includes(q) ||
        (d.candidate_name || '').toLowerCase().includes(q) ||
        (r.operator_name || '').toLowerCase().includes(q) ||
        (d.country || '').toLowerCase().includes(q) ||
        (d.city || '').toLowerCase().includes(q) ||
        (d.subject || '').toLowerCase().includes(q)
      )
    }
    return true
  })

  const pendingCount = requests.filter(r => r.status === 'pending').length

  return (
    <div className="rp-root" style={{ minHeight: '100vh', background: darkMode ? '#0f1117' : '#f7f7f5', fontFamily: 'sans-serif' }}>
      {/* Header */}
      <div className="rp-header" style={{ background: darkMode ? '#1a1f2e' : 'white', borderBottom: `1px solid ${darkMode ? '#2d3448' : '#e8e8e5'}`, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>←</button>
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}><span style={{ fontWeight: 700, fontSize: 18, color: darkMode ? '#e2e8f0' : undefined }}>Заявки</span><SpecialBadge /></div>
          <div style={{ fontSize: 12, color: '#aaa' }}>{requests.length} всего{pendingCount > 0 ? `, ${pendingCount} ожидают` : ''}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '16px 24px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Поиск по кандидату, теме..."
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13, width: 220 }} />

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + (filterStatus ? '#1a1a1a' : (darkMode ? '#2d3448' : '#e0e0dc')), fontSize: 13, background: filterStatus ? '#1a1a1a' : (darkMode ? '#1a1f2e' : 'white'), color: filterStatus ? 'white' : (darkMode ? '#e2e8f0' : '#444') }}>
          <option value="">Все статусы</option>
          {STATUSES.slice(1).map(s => <option key={s} value={s}>{STATUS_LABELS[s]?.label || s}</option>)}
        </select>

        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + (filterType ? '#1a1a1a' : (darkMode ? '#2d3448' : '#e0e0dc')), fontSize: 13, background: filterType ? '#1a1a1a' : (darkMode ? '#1a1f2e' : 'white'), color: filterType ? 'white' : (darkMode ? '#e2e8f0' : '#444') }}>
          <option value="">Все типы</option>
          {TYPES.slice(1).map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>

        {isAdmin && (
          <select value={filterOp} onChange={e => setFilterOp(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + (filterOp ? '#1a1a1a' : (darkMode ? '#2d3448' : '#e0e0dc')), fontSize: 13, background: filterOp ? '#1a1a1a' : (darkMode ? '#1a1f2e' : 'white'), color: filterOp ? 'white' : (darkMode ? '#e2e8f0' : '#444') }}>
            <option value="">Все операторы</option>
            {OPERATORS.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}

        {(filterType || filterStatus || filterOp || search) && (
          <button onClick={() => { setFilterType(''); setFilterStatus(''); setFilterOp(''); setSearch('') }}
            style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#2d3448' : '#e0e0dc'}`, background: darkMode ? '#252b3b' : 'white', color: darkMode ? '#94a3b8' : '#888', fontSize: 12, cursor: 'pointer' }}>
            ✕ Сбросить
          </button>
        )}
      </div>

      {/* List */}
      <div style={{ padding: '0 24px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Заявок не найдено</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(r => {
              const d = r.data || {}
              let detail = ''
              if (r.type === 'ticket') detail = d.from && d.to ? `${d.from} → ${d.to}` : ''
              else if (r.type === 'evisa' || r.type === 'visa') detail = d.country || ''
              else if (r.type === 'hotel') detail = d.city || ''
              else if (r.type === 'free') detail = d.subject || ''
              else if (r.type === 'sos') detail = d.message ? d.message.slice(0, 60) + (d.message.length > 60 ? '...' : '') : ''
              const isSos = r.type === 'sos'

              return (
                <div key={r.id} onClick={() => setSelected(r)}
                  style={{ background: isSos ? (darkMode ? '#3d1520' : '#FEF0F0') : (darkMode ? '#1a1f2e' : 'white'), border: isSos ? '2px solid #E24B4A' : `1px solid ${darkMode ? '#2d3448' : '#e8e8e5'}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
                  onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#aaa', fontWeight: 500 }}>#{r.id}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: darkMode ? '#e2e8f0' : undefined }}>{TYPE_LABELS[r.type] || r.type}</span>
                        {detail && <span style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#666' }}>· {detail}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {d.candidate_name && <span style={{ marginRight: 10 }}>👤 {d.candidate_name}</span>}
                        <span style={{ marginRight: 10 }}>🧑‍💼 {r.operator_name || '—'}</span>
                        <span>📅 {fmtDate(r.created_at)}</span>
                      </div>
                      {r.notes && <div style={{ fontSize: 11, color: '#E24B4A', marginTop: 4 }}>💬 {r.notes}</div>}
                    </div>
                    <Badge status={r.status} />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {selected && (
        <RequestDetail req={selected} onClose={() => setSelected(null)} onStatusChange={handleStatusChange} isAdmin={isAdmin} darkMode={darkMode} />
      )}
    </div>
  )
}
